import { type KeyboardEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BoardPreview, CalendarPreview, DatabasePreview } from './previews';
import styles from './ProductDemo.module.css';

const TABS = ['boards', 'databases', 'calendar'] as const;
type Tab = (typeof TABS)[number];

/**
 * The hero's switchable product illustration: Boards / Databases / Calendar. A standard tab
 * pattern (roving tabindex, arrows, Home / End); only the active panel is mounted. The panel has a
 * fixed height per breakpoint (see the CSS), so switching never moves the page. The illustration
 * itself is hidden from assistive technology and replaced by one sentence describing it.
 */
export function ProductDemo() {
  const { t } = useTranslation();
  const [active, setActive] = useState<Tab>('boards');
  const base = useId();
  const refs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});

  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = TABS.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % TABS.length
        : event.key === 'ArrowLeft'
          ? (index + last) % TABS.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    const tab = TABS[next] ?? 'boards';
    setActive(tab);
    refs.current[tab]?.focus();
  };

  return (
    <div className={styles.demo}>
      <div className={styles.tabs} role="tablist" aria-label={t('landing.demo.label')}>
        {TABS.map((tab, index) => (
          <button
            key={tab}
            ref={(node) => {
              refs.current[tab] = node;
            }}
            type="button"
            role="tab"
            id={`${base}-tab-${tab}`}
            aria-selected={active === tab}
            aria-controls={`${base}-panel`}
            tabIndex={active === tab ? 0 : -1}
            className={styles.tab}
            onClick={() => {
              setActive(tab);
            }}
            onKeyDown={(event) => {
              move(event, index);
            }}
          >
            {t(`landing.demo.tabs.${tab}`)}
          </button>
        ))}
      </div>
      <div className={styles.window}>
        <div className={styles.chrome} aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div
          key={active}
          className={styles.panel}
          role="tabpanel"
          id={`${base}-panel`}
          aria-labelledby={`${base}-tab-${active}`}
          data-testid="demo-panel"
          data-demo={active}
        >
          <p className="visually-hidden">{t(`landing.demo.desc.${active}`)}</p>
          <div className={styles.stage} aria-hidden="true">
            {active === 'boards' && <BoardPreview />}
            {active === 'databases' && <DatabasePreview />}
            {active === 'calendar' && <CalendarPreview />}
          </div>
        </div>
      </div>
      <p className={styles.caption}>{t('landing.demo.caption')}</p>
    </div>
  );
}
